import type { AgentInstance } from '../../store/types'
import {
  sendKillAgent,
  sendRetireAgent,
  sendRenameAgent,
  sendDestroyAgent,
  sendSleepAgent,
  sendWakeAgent,
} from '../../api/ws'
import { SwipeableRow } from '../../components/SwipeableRow'
import { StarIcon, SleepIcon } from '../../components/icons'
import './AgentRow.css'

export type Row = {
  agent: AgentInstance
  positionName: string
  hostname: string
  hostOnline: boolean
  hostDesiredConnected: boolean
}

export type AgentVisualState =
  | 'healthy'
  | 'starting'
  | 'exited'
  | 'killed'
  | 'spawn_failed'
  | 'asleep'
  | 'host_unreachable'
  | 'host_parked'

type SheetGroup = 'operations' | 'lifecycle'

type SheetItem = {
  label: string
  sublabel?: string
  onAction: () => void
  icon?: string
  destructive?: boolean
  disabled?: boolean
  group: SheetGroup
}

/// Collapses (host-online, host-desired, status, pid_status) into a single
/// visual state.
export function agentVisualState(row: Row): AgentVisualState {
  if (!row.hostOnline) {
    return row.hostDesiredConnected ? 'host_unreachable' : 'host_parked'
  }
  const a = row.agent
  if (a.status === 'killed') return 'killed'
  if (a.status === 'asleep') return 'asleep'
  if (a.pid_status === 'spawn_failed') return 'spawn_failed'
  if (a.pid_status === 'never_spawned' || a.pid_status === 'spawning') return 'starting'
  if (a.pid_status === 'exited' || a.pid_status === 'killed') return 'exited'
  return 'healthy'
}

export function confirmAndRetire(row: Row) {
  if (window.confirm(`Retire ${row.agent.name}? This ends the agent and removes it from the list.`)) {
    sendRetireAgent(row.agent.id)
  }
}

export function confirmAndFire(row: Row) {
  if (
    window.confirm(
      `Fire ${rowDisplayName(row)}?\n\nThis ends the agent and PERMANENTLY DELETES the working folder Hearth created for it, and everything in it. (A folder you started it in yourself is left alone.) This cannot be undone.`,
    )
  ) {
    sendDestroyAgent(row.agent.id)
  }
}

// Auto-generated temp names are "Temp <8 hex chars>". If the current name
// still matches, present the prompt empty so the user types fresh.
export const TEMP_AUTO_NAME = /^Temp [0-9a-f]{8}$/

export function promptAndRename(row: Row) {
  const prefill = TEMP_AUTO_NAME.test(row.agent.name) ? '' : row.agent.name
  const next = window.prompt(`Rename ${row.agent.name} to:`, prefill)
  if (next == null) return
  const trimmed = next.trim()
  if (!trimmed || trimmed === row.agent.name) return
  sendRenameAgent(row.agent.id, trimmed)
}

export function AgentRow({
  row,
  onTap,
  onLongPress,
}: {
  row: Row
  onTap: () => void
  onLongPress: () => void
}) {
  const state = agentVisualState(row)
  const destructive = row.agent.is_temp
    ? { label: 'Fire', onAction: () => confirmAndFire(row) }
    : { label: 'Retire', onAction: () => confirmAndRetire(row) }

  const showPosition = !row.agent.is_temp
  const subtitle =
    state === 'host_unreachable'
      ? `Host unreachable · ${row.hostname}`
      : state === 'host_parked'
        ? `Host offline · ${row.hostname}`
        : state === 'starting'
          ? 'Starting…'
          : state === 'exited'
            ? `Exited · ${row.hostname}`
            : state === 'killed'
              ? `Killed · ${row.hostname}`
              : state === 'spawn_failed'
                ? 'Spawn failed'
                : state === 'asleep'
                  ? showPosition
                    ? `Asleep · ${row.positionName}`
                    : 'Asleep'
                  : showPosition
                    ? `${row.positionName} · ${row.hostname}`
                    : row.hostname

  const iconGlyph =
    state === 'starting' ? (
      <span className="agents__row-spinner" />
    ) : state === 'asleep' ? (
      <SleepIcon className="agents__row-sleep-icon" />
    ) : (
      <StarIcon className="agents__row-star-icon" />
    )

  const trailingIndicator =
    state === 'host_unreachable' ? (
      <span className="agents__row-unreachable" aria-hidden>⚠</span>
    ) : state === 'starting' || state === 'asleep' || state === 'host_parked' ? null : (
      <div className="agents__row-state-dot" aria-hidden />
    )

  return (
    <SwipeableRow trailing={destructive} onLongPress={onLongPress}>
      <button
        className={`agents__row agents__row--${state}`}
        onClick={onTap}
        onContextMenu={(e) => { e.preventDefault() }}
      >
        <div className="agents__row-icon" aria-hidden>{iconGlyph}</div>
        <div className="agents__row-body">
          <div className="agents__row-name">{rowDisplayName(row)}</div>
          <div className="agents__row-sub">
            {row.agent.chat_helper_room ? `Helping in #${row.agent.chat_helper_room} · ${subtitle}` : subtitle}
          </div>
        </div>
        {trailingIndicator}
        <div className="agents__row-chevron" aria-hidden>›</div>
      </button>
    </SwipeableRow>
  )
}

// A live quick helper's instance name carries a uniqueness suffix ("Helper
// 3f9a1b2c"); chat calls it plain "Helper", and so does its row.
function rowDisplayName(row: Row): string {
  return row.agent.chat_helper_room ? 'Helper' : row.agent.name
}

export function AgentActionSheet({
  row,
  onClose,
}: {
  row: Row
  onClose: () => void
}) {
  const state = agentVisualState(row)
  const id = row.agent.id
  const items: SheetItem[] = []

  const hostLive = state !== 'host_unreachable' && state !== 'host_parked'

  if (hostLive) {
    if (state === 'killed' || state === 'exited' || state === 'spawn_failed') {
      items.push({ label: 'Re-spawn', icon: '✨', onAction: () => sendWakeAgent(id), group: 'operations' })
    } else if (state === 'asleep') {
      items.push({ label: 'Wake', icon: '✨', onAction: () => sendWakeAgent(id), group: 'operations' })
    }
    if (state === 'healthy') {
      items.push({ label: 'Sleep', icon: '🌙', onAction: () => sendSleepAgent(id), group: 'operations' })
      items.push({
        label: 'Unplug',
        sublabel: '(halt immediately)',
        icon: '🔌',
        onAction: () => {
          if (window.confirm(`Unplug ${row.agent.name}? This forcibly stops the agent immediately.`)) {
            // Safety action: never let a dropped kill frame read as success.
            if (!sendKillAgent(id)) {
              window.alert(
                "Couldn't reach the server to unplug this agent — it was NOT stopped. Check your connection and try again.",
              )
            }
          }
        },
        destructive: true,
        group: 'operations',
      })
    }
  }

  if (import.meta.env.VITE_DEV_DEPLOY) {
    items.push({
      label: 'Copy ID',
      onAction: () => { void navigator.clipboard?.writeText(id).catch(() => {}) },
      group: 'operations',
    })
  }

  const isTemp = row.agent.is_temp
  if (isTemp) {
    items.push({ label: 'Rename', icon: '✏️', onAction: () => promptAndRename(row), group: 'lifecycle' })
    items.push({ label: 'Fire', icon: '🔥', onAction: () => confirmAndFire(row), destructive: true, group: 'lifecycle' })
  } else {
    items.push({ label: 'Retire', icon: '👋', onAction: () => confirmAndRetire(row), destructive: true, group: 'lifecycle' })
  }

  function fire(item: SheetItem) {
    onClose()
    item.onAction()
  }

  return (
    <div
      className="agents__picker"
      role="dialog"
      aria-label={`Actions for ${row.agent.name}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="agents__picker-sheet">
        <div className="agents__picker-title">{row.agent.name}</div>
        {items.map((item, i) => {
          const divider = i > 0 && items[i - 1].group !== item.group
          const cls = [
            'agents__picker-row',
            item.destructive && 'agents__picker-row--destructive',
            item.disabled && 'agents__picker-row--disabled',
          ].filter(Boolean).join(' ')
          return (
            <div key={item.label}>
              {divider && <div className="agents__picker-divider" />}
              <button className={cls} onClick={() => !item.disabled && fire(item)} disabled={item.disabled}>
                {item.icon && <span className="agents__picker-row-icon" aria-hidden>{item.icon}</span>}
                <span className="agents__picker-row-label">{item.label}</span>
                {item.sublabel && <span className="agents__picker-row-sublabel">{item.sublabel}</span>}
              </button>
            </div>
          )
        })}
        <button className="agents__picker-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}
